import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Pause, Play } from "lucide-react";
import { BACKEND, boApi, getToken, inr, ist, tone, dec } from "../api";
import { useBo } from "../AdminAuth";
import { DataTable } from "../DataTable";
import { ActionButton } from "../ActionDialog";
import { Card, Kpi, PageHead, Pill, Spinner } from "../ui";

const clientCol = { key: "client_name", label: "Client", sortable: false, render: (v, r) => <Link to={`/admin/clients/${r.user_id}`} className="hover:text-primary">{v} <span className="text-muted-foreground">{r.client_code}</span></Link> };

function RmsSettings() {
  const [s, setS] = useState(null);
  const load = () => boApi.get("/risk/settings").then((r) => setS(r.data));
  useEffect(() => { load(); }, []);
  if (!s) return <Spinner />;
  const fields = [{ key: "rms_loss_pct", label: "Auto square-off at MTM loss % of funds", type: "number" }, { key: "mis_cutoff", label: "Block new MIS orders after (HH:MM IST)" },
    { key: "max_order_value", label: "Max order value ₹", type: "number" }, { key: "max_open_positions", label: "Max open positions per client (0 = no limit)", type: "number" },
    { key: "max_lots", label: "Max lots per order (0 = no limit)", type: "number" }, { key: "fo_ban", label: "F&O ban list (underlying symbols, comma separated)" },
    { key: "disabled_segments", label: "Disabled segments", type: "multi", options: ["NSE_EQ", "BSE_EQ", "NSE_FO", "BSE_FO", "MCX_FO"] }];
  return (
    <Card title="Global RMS settings" testid="rms-settings" right={<ActionButton perm="risk.write" testid="edit-rms-btn" label="Edit" onDone={load} fields={fields} initial={{ ...s, fo_ban: s.fo_ban.join(", ") }}
      onSubmit={(v, reason) => boApi.put("/risk/settings", { ...v, fo_ban: String(v.fo_ban || "").split(",").map((x) => x.trim()).filter(Boolean), surv: s.surv, reason }).then((r) => r.data)} />}>
      <div className="grid gap-2 text-xs sm:grid-cols-2 lg:grid-cols-4">
        {fields.map((f) => <div key={f.key}><div className="text-muted-foreground">{f.label}</div><div className="num font-semibold">{Array.isArray(s[f.key]) ? s[f.key].join(", ") || "—" : String(s[f.key])}</div></div>)}
        {Object.entries(s.square_off_times).map(([k, v]) => <div key={k}><div className="text-muted-foreground">Auto square-off · {k}</div><div className="font-semibold">{v}</div></div>)}
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">Client-level overrides are set on each client's profile (Limits). Square-off times run on the platform schedule.</p>
    </Card>
  );
}

function KillSwitches() {
  const [s, setS] = useState(null);
  const load = () => boApi.get("/risk/settings").then((r) => setS(r.data));
  useEffect(() => { load(); }, []);
  const k = (scope, on, extra = {}) => (v, reason) => boApi.post("/risk/kill", { scope, on, reason, ...extra, ...v }).then((r) => r.data);
  if (!s) return null;
  return (
    <Card title="Kill switches · exit orders are always allowed" testid="kill-switches" className={s.kill_platform ? "border-red-500/60" : ""}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span>Platform: <Pill testid="kill-platform-state" value={s.kill_platform ? "BLOCKED" : "ACTIVE"} /></span>
        {s.kill_platform ? <ActionButton perm="risk.write" testid="kill-platform-off" label="Allow new orders" onDone={load} onSubmit={k("platform", false)} />
          : <ActionButton perm="risk.write" testid="kill-platform-on" label="Block all new orders" danger onDone={load} description="Needs approval from a second admin. Clients see the banner message." fields={[{ key: "message", label: "Banner message to clients" }]} onSubmit={k("platform", true)} />}
        <ActionButton perm="risk.write" testid="kill-client-btn" label="Block a client" danger fields={[{ key: "target", label: "Client internal ID" }]} onSubmit={k("client", true)} />
        <ActionButton perm="risk.write" testid="kill-instrument-btn" label="Block an instrument" danger fields={[{ key: "target", label: "Instrument key (e.g. NSE_EQ|INE002A01018)" }]} onSubmit={(v, r) => k("instrument", true)(v, r).then((x) => { load(); return x; })} />
        {s.blocked_instruments.map((i) => <ActionButton key={i} perm="risk.write" testid={`unblock-inst-${i}`} label={`Unblock ${i}`} onDone={load} onSubmit={k("instrument", false, { target: i })} />)}
      </div>
    </Card>
  );
}

export function Risk() {
  return (
    <div className="space-y-4">
      <PageHead title="Risk & RMS" sub="Clients sorted by risk. Colours: normal / warning / critical." />
      <KillSwitches />
      <DataTable id="risk-clients" local endpoint="/risk/clients" rowKey="user_id"
        columns={[clientCol, { key: "level", label: "Level", render: (v) => <Pill value={v} /> }, { key: "mtm", label: "MTM", right: true, render: (v) => <span className={tone(v)}>{inr(v)}</span> },
          { key: "loss_pct", label: "MTM loss % of funds", right: true, render: dec }, { key: "margin_util_pct", label: "Margin used %", right: true, render: dec },
          { key: "distance_to_sqoff_pct", label: "Distance to auto sq-off (pts)", right: true, render: dec }, { key: "available_margin", label: "Available", right: true, render: inr }, { key: "open_positions", label: "Open pos.", right: true }]} />
      <RmsSettings />
      <Card title="Auto square-off log">
        <DataTable id="sqoff-log" endpoint="/risk/sqoff-log" columns={[{ key: "created_at", label: "Time", render: ist }, clientCol, { key: "symbol", label: "Symbol" }, { key: "side", label: "Side" }, { key: "quantity", label: "Qty", right: true }, { key: "status", label: "Status", render: (v) => <Pill value={v} /> }, { key: "reason_text", label: "Reason", sortable: false }]} />
      </Card>
    </div>
  );
}

export function Surveillance() {
  return (
    <div>
      <PageHead title="Surveillance" sub="Checked every minute: rapid loss, bot-like order rate, repeated rejections, unusually large orders, linked accounts trading together." />
      <DataTable id="surveillance" endpoint="/surveillance" filters={[{ key: "status", label: "Status", type: "select", options: ["OPEN", "REVIEWED", "CLOSED"] }, { key: "kind", label: "Kind", type: "select", options: ["rapid_loss", "order_rate", "rejections", "large_order", "linked_trades"] }]}
        columns={[{ key: "created_at", label: "Raised", render: ist }, clientCol, { key: "kind", label: "Kind" }, { key: "detail", label: "Detail" }, { key: "status", label: "Status", render: (v) => <Pill value={v} /> },
          { key: "notes", label: "Notes", sortable: false, render: (v) => (v || []).map((n) => `${n.by}: ${n.text}`).join(" | ") || "—" }]}
        actions={(r, reload) => r.status !== "CLOSED" && <ActionButton perm="surv.write" testid={`surv-update-${r.id}`} label="Update" noReason onDone={reload} initial={{ status: r.status === "OPEN" ? "REVIEWED" : "CLOSED" }}
          fields={[{ key: "status", label: "Status", type: "select", options: ["OPEN", "REVIEWED", "CLOSED"] }, { key: "note", label: "Note (required)", type: "textarea" }]} onSubmit={(v) => boApi.post(`/surveillance/${r.id}`, v).then((x) => x.data)} />} />
    </div>
  );
}

function Jobs() {
  const [j, setJ] = useState([]);
  const load = () => boApi.get("/monitor/jobs").then((r) => setJ(r.data));
  useEffect(() => { load(); }, []);
  return (
    <Card title="Background jobs" testid="jobs-panel">
      <table className="w-full text-xs"><tbody>
        {j.map((x) => (
          <tr key={x.job} className="border-t border-border/40">
            <td className="py-1.5">{x.label}</td><td>{x.last ? <Pill value={x.last.status} /> : <span className="text-muted-foreground">{x.note || "no run yet"}</span>}</td>
            <td className="text-muted-foreground">{x.last ? `${ist(x.last.started_at)} · ${x.last.result || ""}` : ""}</td>
            <td className="text-right">{x.job !== "t1-settlement" && <ActionButton perm="ops.write" testid={`rerun-${x.job}`} label="Re-run" onDone={() => setTimeout(load, 1500)} onSubmit={(_, reason) => boApi.post(`/monitor/jobs/${x.job}/rerun`, { reason }).then((r) => r.data)} />}</td>
          </tr>
        ))}
      </tbody></table>
    </Card>
  );
}

export function LiveMonitor() {
  const { can } = useBo();
  const [s, setS] = useState(null);
  const [feed, setFeed] = useState([]);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  pausedRef.current = paused;
  useEffect(() => {
    const ws = new WebSocket(`${BACKEND.replace(/^http/, "ws")}/api/bo/ws?token=${getToken()}`);
    ws.onmessage = (e) => {
      const m = JSON.parse(e.data);
      setS(m.stats);
      if (!pausedRef.current) {
        const items = [...m.orders.map((o) => ({ k: `o${o.id}`, at: o.created_at, t: "ORDER", text: `${o.client_name} ${o.side} ${o.quantity} ${o.symbol} ${o.order_type}`, st: o.status })),
          ...m.fills.map((f) => ({ k: `f${f.id}`, at: f.at, t: "FILL", text: `${f.client_name} ${f.side} ${f.qty} ${f.symbol} @ ${f.price}`, st: "COMPLETE" }))];
        if (items.length) setFeed((cur) => [...items.sort((a, b) => (a.at < b.at ? 1 : -1)), ...cur].slice(0, 200));
      }
    };
    return () => ws.close();
  }, []);
  return (
    <div className="space-y-4">
      <PageHead title="Live monitor" sub="Streams every 2 seconds over a WebSocket." />
      {!s ? <Spinner /> : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6" data-testid="monitor-stats">
          <Kpi label="Clients online" value={s.clients_online} /><Kpi label="WebSocket connections" value={s.ws_connections} /><Kpi label="Orders / min" value={s.orders_min} />
          <Kpi label="Fills / min" value={s.fills_min} /><Kpi label="Rejections / min" value={s.rejections_min} /><Kpi label="Pending order queue" value={s.pending_queue} />
          <Kpi label="Matching p50 / p95" value={`${s.match_p50_ms ?? "—"} / ${s.match_p95_ms ?? "—"} ms`} /><Kpi label="Ticks / s" value={s.ticks_per_s ?? "—"} />
          <Kpi label="Feed" value={<Pill value={String(s.feed).toUpperCase()} />} sub={s.feed_state} /><Kpi label="Last tick age" value={s.tick_age_s != null ? `${s.tick_age_s}s` : "—"} />
        </div>
      )}
      <Card title="Latest orders & fills" testid="live-feed" right={<button data-testid="feed-pause-btn" onClick={() => setPaused(!paused)} className="flex items-center gap-1 text-xs">{paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}{paused ? "Resume" : "Pause"}</button>}>
        <div className="max-h-80 space-y-0.5 overflow-y-auto text-xs">
          {feed.length === 0 && <div className="text-muted-foreground">Waiting for activity…</div>}
          {feed.map((f) => <div key={f.k} className="flex gap-3 border-b border-border/30 py-0.5"><span className="num text-muted-foreground">{ist(f.at).slice(-8)}</span><span className={f.t === "FILL" ? "text-up" : "text-primary"}>{f.t}</span><span className="flex-1">{f.text}</span><Pill value={f.st} /></div>)}
        </div>
      </Card>
      {can("monitor.view") && <Jobs />}
    </div>
  );
}
